import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, DoorOpen, Wand2, X, AlertCircle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import Portal from "./Portal";

const CH_STYLE = { Atiti: "bg-[#EEF2FF] text-[#4F46E5]", "Booking.com": "bg-sky-50 text-sky-600", Airbnb: "bg-violet-50 text-violet-600", Direct: "bg-emerald-50 text-emerald-600", MakeMyTrip: "bg-red-50 text-red-500" };
const fmt = (n) => "₹" + Number(n).toLocaleString("en-IN");

export default function UnassignedPanel({ onChanged }) {
  const [data, setData] = useState(null);
  const [assign, setAssign] = useState(null); // { booking, property_id, unit_id, propList }
  const [busy, setBusy] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(() => {
    setData(null);
    api.get("/pms/unassigned").then(({ data: d }) => setData(d))
      .catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const seedOne = async () => {
    setSeeding(true);
    try {
      const { data: b } = await api.post("/pms/seed-unassigned");
      toast.success(`Demo arrival from ${b.channel} · ${b.guest} — needs a unit`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setSeeding(false);
  };

  const openAssign = async (b) => {
    setAssign({ booking: b, property_id: "", unit_id: "", propList: null, unitList: [] });
    try {
      const { data: ov } = await api.get("/pms/overview");
      const props = ov.properties.filter((p) => p.status === "active");
      setAssign((s) => ({ ...s, propList: props }));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const selectProperty = async (pid) => {
    setAssign((s) => ({ ...s, property_id: pid, unit_id: "", unitList: null }));
    try {
      const { data: res } = await api.get(`/pms/properties/${pid}/reservations`);
      setAssign((s) => ({ ...s, unitList: res.bookable_units || [] }));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submit = async () => {
    if (!assign.property_id || !assign.unit_id) return toast.error("Pick property and unit");
    setBusy(true);
    try {
      const { data: r } = await api.post(`/pms/bookings/${assign.booking.id}/assign`, {
        property_id: assign.property_id, unit_id: assign.unit_id,
      });
      toast.success(`${assign.booking.guest} assigned to ${r.property_name} · Unit ${r.unit_name} — future ${assign.booking.channel} arrivals flow here too`);
      setAssign(null);
      load(); onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (!data) return null;
  if (data.count === 0) return (
    <div className="bg-white border border-slate-200 rounded-2xl px-5 py-4 flex items-center justify-between text-sm text-slate-500">
      <span>No unassigned bookings — every OTA arrival is mapped to a physical unit.</span>
      <button data-testid="unassigned-seed" onClick={seedOne} disabled={seeding} className="text-xs px-4 py-1.5 rounded-full border border-slate-200 hover:border-slate-400 flex items-center gap-1.5">
        {seeding ? <Loader2 className="animate-spin" size={12} /> : <Wand2 size={12} />} Simulate an OTA arrival without a unit
      </button>
    </div>
  );

  return (
    <>
      <div className="bg-amber-50 border border-amber-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-amber-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle size={15} className="text-amber-600" />
            <span className="font-display font-bold text-amber-900 text-sm">{data.count} OTA arrival{data.count > 1 ? "s" : ""} waiting for a unit</span>
          </div>
          <button data-testid="unassigned-seed-more" onClick={seedOne} disabled={seeding} className="text-[11px] px-3 py-1.5 rounded-full bg-white border border-amber-200 text-amber-700 hover:border-amber-400 flex items-center gap-1.5">
            {seeding ? <Loader2 className="animate-spin" size={11} /> : <Wand2 size={11} />} Simulate one more
          </button>
        </div>
        <div className="divide-y divide-amber-100">
          {data.unassigned.map((b) => (
            <div key={b.id} data-testid={`unassigned-row-${b.id}`} className="px-5 py-3 flex items-center gap-4 flex-wrap hover:bg-amber-100/40 transition-colors">
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${CH_STYLE[b.channel] || "bg-slate-100 text-slate-500"}`}>{b.channel}</span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-800 text-sm truncate">{b.guest}</p>
                <p className="text-[10px] text-slate-500 font-mono">{b.checkin} → {b.checkout} · {b.nights}n · {b.listing_title}</p>
              </div>
              <span className="font-mono text-sm text-slate-700">{fmt(b.amount)}</span>
              <button data-testid={`assign-${b.id}`} onClick={() => openAssign(b)}
                className="btn-brand rounded-full px-4 py-2 text-[11px] font-semibold flex items-center gap-1.5"><DoorOpen size={12} /> Assign Unit</button>
            </div>
          ))}
        </div>
      </div>

      {assign && (
      <Portal>
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[80] grid place-items-center p-4 sm:p-6 overflow-y-auto" onClick={() => setAssign(null)}>
          <div data-testid="assign-drawer" className="bg-white w-[96vw] min-h-[92vh] max-h-[92vh] max-w-none rounded-2xl shadow-2xl p-8 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2"><DoorOpen size={16} className="brand-pink" /> Assign Unit</h3>
              <button data-testid="assign-close" onClick={() => setAssign(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="mt-4 bg-slate-50 border border-slate-100 rounded-xl p-4 text-sm">
              <p className="font-semibold text-slate-800">{assign.booking.guest}</p>
              <p className="text-xs text-slate-500 mt-0.5">{assign.booking.channel} · {assign.booking.checkin} → {assign.booking.checkout} ({assign.booking.nights}n) · {fmt(assign.booking.amount)}</p>
            </div>
            <div className="mt-5 space-y-4">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Property</label>
                <select data-testid="assign-property" value={assign.property_id} onChange={(e) => selectProperty(e.target.value)}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40">
                  <option value="">Select property…</option>
                  {(assign.propList || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              {assign.property_id && (
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Physical unit</label>
                  <select data-testid="assign-unit" value={assign.unit_id} onChange={(e) => setAssign({ ...assign, unit_id: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40">
                    <option value="">Select unit…</option>
                    {(assign.unitList || []).map((u) => <option key={u.unit_id} value={u.unit_id}>Unit {u.name}</option>)}
                  </select>
                </div>
              )}
              <button data-testid="assign-submit" onClick={submit} disabled={busy || !assign.unit_id}
                className="btn-brand w-full rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin" size={14} /> : <DoorOpen size={14} />} Assign & sync
              </button>
              <p className="text-[10px] text-slate-400 leading-tight">Double Booking Guard runs the moment you click — stopsells, overlaps and blocking-group conflicts refuse the assignment. Once assigned, future arrivals from this channel flow into the same unit automatically.</p>
            </div>
          </div>
        </div>
      </Portal>
      )}
    </>
  );
}
